import Vue from 'vue'
import util from '@/js/Util'
import moment from 'moment'

/*
  TODO
  Duplicated from mixing function. This should go into util class, but needs to
  get unit strings from store.
 */
function formatKWH(value, units) {
  if (value > 990000 || value < -990000) {
    value = (value / 1000000).toFixed(1)
    value = (value + '').replace(/\./, ',')
    return `${value} ${units.megawattHours}`
  }

  if (value > 990 || value < -990) {
    value = (value / 1000).toFixed(1)
    value = (value + '').replace(/\./, ',')
    return `${value} ${units.kilowattHours}`
  }

  value = (value.toFixed(1) + '').replace(/\./, ',')
  return `${value} ${units.wattHours}`
}

function cubeConnectionsReceived(state, data) {
  const producers = Object.keys(data.data.producers).map((cubeName) => {
    const rawCube = {
      cubeName: cubeName,
      ...data.data.producers[cubeName],
    }

    return util.preprocessCube(rawCube)
  })

  Vue.set(state.cube, 'producers', producers)

  const consumers = Object.keys(data.data.consumers).map((cubeName) => {
    const rawCube = {
      cubeName: cubeName,
      ...data.data.consumers[cubeName],
    }

    return util.preprocessCube(rawCube)
  })
  Vue.set(state.cube, 'consumers', consumers)
  Vue.set(state.cube, 'connectionsReceived', true)
}

function globalCubesListReceived(state, data) {
  const cubes = Object.keys(data.data).map((cubeName) => {
    let cube = {
      cubeName: cubeName,
      mapPosition: {
        lat: parseFloat(data.data[cubeName].params.latitude),
        lng: parseFloat(data.data[cubeName].params.longitude),
      },
      ...data.data[cubeName],
    }

    return util.preprocessCube(cube)
  })
  Vue.set(state.map, 'globalCubesList', cubes)
}

function connectedToProducer(state, data) {
  const cubeName = data.producer.cubeName

  // Add the producer to the list of producers.
  state.cube.producers.push(data.producer)

  // Update the producer's connection status in the global cubes list.
  const globalIndex = state.map.globalCubesList.findIndex((cube) => {
    return cube.cubeName === cubeName
  })

  if (globalIndex === -1) {
    return false
  }

  let globalProducer = state.map.globalCubesList[globalIndex]
  globalProducer.isProducer = true
  Vue.set(state.map.globalCubesList, globalIndex, globalProducer)
  Vue.set(state.cube, 'unusedEnergyLines', data.unusedEnergyLines)
}

function donatingToConsumer(state, data) {
  const cubeName = data.consumer.cubeName

  // Update the consumer's connection status in the global cubes list.
  const globalIndex = state.map.globalCubesList.findIndex((cube) => {
    return cube.cubeName === cubeName
  })

  if (globalIndex === -1) {
    return false
  }

  let globalConsumer = state.map.globalCubesList[globalIndex]
  globalConsumer.isDonatee = true
  globalConsumer.openConnectionsAvailable -= 1
  Vue.set(state.map.globalCubesList, globalIndex, globalConsumer)
}

function stopDonatingToConsumer(state, consumer) {
  const consumerIndex = state.cube.consumers.findIndex((cube) => {
    return cube.cubeName === consumer.cubeName
  })

  if (consumerIndex > -1) {
    state.cube.consumers.splice(consumerIndex, 1)
  }

  // Update the consumer's connection status in the global cubes list.
  const globalIndex = state.map.globalCubesList.findIndex((cube) => {
    return cube.cubeName === consumer.cubeName
  })

  if (globalIndex === -1) {
    throw new Error(
      `stopDonatingToConsumer(): Could not find consumer ${consumer.cubeName} in globalCubesList`
    )
  }

  let globalConsumer = state.map.globalCubesList[globalIndex]
  globalConsumer.isDonatee = false
  globalConsumer.openConnectionsAvailable += 1
  Vue.set(state.map.globalCubesList, globalIndex, globalConsumer)
}

function disconnectedFromProducer(state, data) {
  const producerIndex = state.cube.producers.findIndex((cube) => {
    return cube.cubeName === data.cubeName
  })

  if (producerIndex > -1) {
    state.cube.producers.splice(producerIndex, 1)
  }

  // Update this cube's status in the global cubes list.
  const globalIndex = state.map.globalCubesList.findIndex((cube) => {
    return cube.cubeName === data.cubeName
  })
  if (globalIndex === -1) {
    return false
  }

  let producer = state.map.globalCubesList[globalIndex]
  producer.isProducer = false
  // Add the newly available connection.
  producer.openConnectionsAvailable += 1
  Vue.set(state.map.globalCubesList, globalIndex, producer)
  Vue.set(state.cube, 'unusedEnergyLines', data.unusedEnergyLines)
}

function historyDataReceived(state, rawData) {
  let lastRowDate
  let data = rawData.data.reduce(
    (result, row) => {
      lastRowDate = moment(row.periodFrom).toDate()
      result.labels.push(util.formatDataPointDate(lastRowDate))

      const fromSupplier = parseFloat(row.power.fromSupplier)
      if (fromSupplier > 0) {
        result.supplier.push(fromSupplier)
      } else {
        const toSupplier = parseFloat(row.power.toSupplier) * -1
        result.supplier.push(toSupplier)
      }

      const fromCommunity = parseFloat(row.power.fromCommunity)
      if (fromCommunity > 0) {
        result.community.push(fromCommunity)
      } else {
        const toCommunity = parseFloat(row.power.toCommunity) * -1
        result.community.push(toCommunity)
      }

      // We turn the community consumable values also negative to also show it on
      // the negative axis.
      const communityConsumable = parseFloat(row.power.communityConsumable) * -1
      result.communityConsumable.push(communityConsumable)

      // add inverterdata
      const inverterProduction = parseInt(row.power.inverterProduction)
      result.inverterProduction.push(inverterProduction)

      return result
    },
    {
      labels: [],
      supplier: [],
      community: [],
      communityConsumable: [],
      inverterProduction: [],
    }
  )

  let requiredRows = 0
  if (rawData.granularity === '15min') {
    // We want to show a full day for 15 minute data.
    requiredRows = 4 * 24
  } else if (rawData.granularity === '1min') {
    // Show an hour.
    requiredRows = 60
  }

  const inc = rawData.granularity === '15min' ? 15 : 1
  while (data.labels.length < requiredRows) {
    lastRowDate = moment(lastRowDate).add(inc, 'minutes').toDate()
    data.labels.push(util.formatDataPointDate(lastRowDate))
    data.supplier.push(0)
    data.community.push(0)
    data.communityConsumable.push(0)
    data.inverterProduction.push(0)
  }

  Vue.set(state.historyData, 'labels', data.labels)
  Vue.set(state.historyData.datasets, 'supplier', data.supplier)
  Vue.set(state.historyData.datasets, 'community', data.community)
  Vue.set(
    state.historyData.datasets,
    'communityConsumable',
    data.communityConsumable
  )
  Vue.set(
    state.historyData.datasets,
    'inverterProduction',
    data.inverterProduction
  )

  state.historyData.received = true
}

function tradingStatisticsReceived(state, data) {
  const colors = {
    pv: '#fed600',
    water: '#1966b7',
    wind: '#ff7d2f',
    biogas: '#69c138',
    supplier: '#9acaff',
    unknown: '#dddddd',
  }

  const units = state.strings.units
  let pvLabel = state.strings.energyTypes.pv
  if (data.energyTypeData.pv > 0) {
    pvLabel += ` (${formatKWH(data.energyTypeData.pv * 1000, units)})`
  }

  let waterLabel = state.strings.energyTypes.water
  if (data.energyTypeData.water > 0) {
    waterLabel += ` (${formatKWH(data.energyTypeData.water * 1000, units)})`
  }

  let windLabel = state.strings.energyTypes.wind
  if (data.energyTypeData.wind > 0) {
    windLabel += ` (${formatKWH(data.energyTypeData.wind * 1000, units)})`
  }

  let biogasLabel = state.strings.energyTypes.biogas
  if (data.energyTypeData.biogas > 0) {
    biogasLabel += ` (${formatKWH(data.energyTypeData.biogas * 1000, units)})`
  }

  let supplierLabel = state.strings.energyTypes.supplier
  if (data.aggregates.consumed_from_retailer > 0) {
    supplierLabel += ` (${formatKWH(
      data.aggregates.consumed_from_retailer * 1000,
      units
    )})`
  }

  const energyTypes = [
    {
      value: data.energyTypeData.pv,
      label: pvLabel,
      color: colors.pv,
    },
    {
      value: data.energyTypeData.water,
      label: waterLabel,
      color: colors.water,
    },
    {
      value: data.energyTypeData.wind,
      label: windLabel,
      color: colors.wind,
    },
    {
      value: data.energyTypeData.biogas,
      label: biogasLabel,
      color: colors.biogas,
    },
    {
      value: data.aggregates.consumed_from_retailer,
      label: supplierLabel,
      color: colors.supplier,
    },
  ]

  let consumedEnergyTypes = {
    data: [],
    labels: [],
    backgroundColor: [],
  }

  energyTypes.map((row) => {
    if (row.value > 0) {
      consumedEnergyTypes.data.push(row.value)
      consumedEnergyTypes.labels.push(row.label)
      consumedEnergyTypes.backgroundColor.push(row.color)
    }
  })
  Vue.set(
    state.tradingStatistics.datasets,
    'consumedEnergyTypes',
    consumedEnergyTypes
  )

  let producedEnergy = {
    data: [],
    labels: [],
    backgroundColor: [],
  }
  producedEnergy.data.push(data.aggregates.sold_to_community * 1000)
  producedEnergy.labels.push(
    `Community (${formatKWH(data.aggregates.sold_to_community * 1000, units)})`
  )
  producedEnergy.backgroundColor.push(colors['pv'])
  producedEnergy.data.push(data.aggregates.sold_to_retailer * 1000)
  producedEnergy.labels.push(
    `eFriends Energy (${formatKWH(
      data.aggregates.sold_to_retailer * 1000,
      units
    )})`
  )
  producedEnergy.backgroundColor.push(colors['supplier'])
  Vue.set(state.tradingStatistics.datasets, 'producedEnergy', producedEnergy)

  Vue.set(
    state.tradingStatistics.datasets,
    'consumedEnergyTypes',
    consumedEnergyTypes
  )

  let producers = {
    data: [],
    labels: [],
    backgroundColor: [],
    raw: [],
  }

  Object.entries(data.incomingTrades).map((row) => {
    if (row[1].consumed > 0.1) {
      producers.data.push(row[1].consumed)
      producers.labels.push(`${row[1].nickName}`)
      producers.backgroundColor.push(colors[row[1].energyType])
      producers.raw.push(row[1])
    }
  })
  Vue.set(state.tradingStatistics.datasets, 'producers', producers)

  let consumers = {
    data: [],
    labels: [],
    backgroundColor: [],
    raw: [],
  }

  Object.entries(data.outgoingTrades).map((row) => {
    if (row[1].sold > 0.1) {
      consumers.data.push(row[1].sold)
      consumers.labels.push(`${row[1].nickName}`)
      consumers.backgroundColor.push(colors[state.cube.productionType])
      consumers.raw.push(row[1])
    }
  })
  Vue.set(state.tradingStatistics.datasets, 'consumers', consumers)

  Vue.set(
    state.tradingStatistics,
    'lastUpdate',
    new Date(data.aggregates.period_to)
  )

  Vue.set(
    state.tradingStatistics,
    'consumedFromCommunity',
    data.aggregates.consumed_from_community * 1000
  )
  Vue.set(
    state.tradingStatistics,
    'consumedFromRetailer',
    data.aggregates.consumed_from_retailer * 1000
  )

  Vue.set(
    state.tradingStatistics,
    'producedForCommunity',
    data.aggregates.sold_to_community * 1000
  )
  Vue.set(
    state.tradingStatistics,
    'producedForRetailer',
    data.aggregates.sold_to_retailer * 1000
  )

  Vue.set(state.tradingStatistics, 'received', true)
}

function setHistorySelectedDate(state, date) {
  Vue.set(state, 'historySelectedDate', date)
}

function setConnectionPriority(state, data) {
  const cubes = state.cube[data.connectionType]
  let cubeIndex = cubes.findIndex((c) => {
    return c.cubeName === data.cubeName
  })
  if (cubeIndex === -1) {
    return
  }
  Vue.set(
    state.cube[data.connectionType][cubeIndex].tradingDetails,
    'priority',
    data.priority
  )
}

export default {
  cubeConnectionsReceived,
  globalCubesListReceived,
  connectedToProducer,
  disconnectedFromProducer,
  historyDataReceived,
  donatingToConsumer,
  stopDonatingToConsumer,
  tradingStatisticsReceived,
  setHistorySelectedDate,
  setConnectionPriority,
}
